import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays } from "lucide-react";

const TYPE_META = {
  new: { label: "New", dot: "bg-blue-500", chip: "bg-blue-50 text-blue-700 border-blue-200" },
  follow_up: { label: "Follow-up", dot: "bg-amber-500", chip: "bg-amber-50 text-amber-800 border-amber-200" },
  converted: { label: "Converted", dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  dead: { label: "Dead", dot: "bg-rose-500", chip: "bg-rose-50 text-rose-700 border-rose-200" },
};
const PERIODS = [
  { v: "week", l: "This Week" },
  { v: "month", l: "This Month" },
  { v: "quarter", l: "This Quarter" },
];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const iso = (d) => d.toISOString().slice(0, 10);

export default function LeadsActivityCalendar({ users = [] }) {
  const [period, setPeriod] = useState("month");
  const [ownerId, setOwnerId] = useState("all");
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const r = await api.get(`/leads/calendar?period=${period}&owner_id=${ownerId}`);
      setData(r.data);
    })();
  }, [period, ownerId]);

  // Build weeks (Mon–Sun rows) spanning the returned range
  const weeks = useMemo(() => {
    if (!data) return [];
    const start = new Date(data.start + "T00:00:00");
    const end = new Date(data.end + "T00:00:00");
    const gridStart = new Date(start);
    gridStart.setDate(start.getDate() - ((start.getDay() + 6) % 7)); // back to Monday
    const gridEnd = new Date(end);
    gridEnd.setDate(end.getDate() + (7 - ((end.getDay() + 6) % 7) - 1)); // forward to Sunday
    const byDate = {};
    (data.activities || []).forEach((a) => { (byDate[a.date] = byDate[a.date] || []).push(a); });
    const rows = [];
    let cur = new Date(gridStart);
    const today = iso(new Date());
    while (cur <= gridEnd) {
      const row = [];
      for (let i = 0; i < 7; i++) {
        const ds = iso(cur);
        row.push({
          date: ds,
          day: cur.getDate(),
          inRange: cur >= start && cur <= end,
          isToday: ds === today,
          items: byDate[ds] || [],
        });
        cur.setDate(cur.getDate() + 1);
      }
      rows.push(row);
    }
    return rows;
  }, [data]);

  const totals = useMemo(() => {
    const t = { new: 0, follow_up: 0, converted: 0, dead: 0 };
    (data?.activities || []).forEach((a) => { t[a.type] = (t[a.type] || 0) + 1; });
    return t;
  }, [data]);

  const staff = (users || []).filter((u) => u.user_id && u.user_id !== "all");

  return (
    <Card data-testid="leads-activity-calendar">
      <CardHeader className="border-b border-slate-100">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-blue-600" /> Activity calendar
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="hidden sm:flex items-center gap-3 mr-2 text-[11px]">
              {Object.entries(TYPE_META).map(([k, m]) => (
                <span key={k} className="inline-flex items-center gap-1 text-slate-500">
                  <span className={`h-2 w-2 rounded-full ${m.dot}`} /> {m.label} <b className="text-slate-700">{totals[k] || 0}</b>
                </span>
              ))}
            </div>
            <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid="calendar-period-toggle">
              {PERIODS.map((p) => (
                <button
                  key={p.v}
                  onClick={() => setPeriod(p.v)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${period === p.v ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                  data-testid={`calendar-period-${p.v}`}
                >
                  {p.l}
                </button>
              ))}
            </div>
            <Select value={ownerId} onValueChange={setOwnerId}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="calendar-owner-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All people</SelectItem>
                {staff.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-3">
        <div className="grid grid-cols-7 gap-px mb-1">
          {DOW.map((d) => <div key={d} className="text-[10px] font-mono uppercase tracking-wider text-slate-400 text-center py-1">{d}</div>)}
        </div>
        <div className="space-y-px" data-testid="calendar-grid">
          {weeks.map((row, ri) => (
            <div key={ri} className="grid grid-cols-7 gap-px">
              {row.map((cell) => (
                <div
                  key={cell.date}
                  className={`min-h-[76px] rounded-md border p-1 ${cell.inRange ? "bg-white border-slate-200" : "bg-slate-50 border-slate-100 opacity-50"} ${cell.isToday ? "ring-2 ring-blue-400" : ""}`}
                  data-testid={`calendar-day-${cell.date}`}
                >
                  <div className={`text-[10px] font-mono mb-0.5 ${cell.isToday ? "text-blue-700 font-bold" : "text-slate-400"}`}>{cell.day}</div>
                  <div className="space-y-0.5">
                    {cell.items.slice(0, 4).map((a, i) => {
                      const m = TYPE_META[a.type] || {};
                      return (
                        <div key={i} className={`text-[10px] leading-tight px-1 py-0.5 rounded border truncate ${m.chip}`} title={`${m.label}: ${a.lead_name}${a.owner_name ? " · " + a.owner_name : ""}`}>
                          {a.lead_name}
                        </div>
                      );
                    })}
                    {cell.items.length > 4 && <div className="text-[9px] text-slate-400 px-1">+{cell.items.length - 4} more</div>}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
        {(data?.activities || []).length === 0 && (
          <div className="text-center text-xs text-slate-400 py-6">No lead activity in this period.</div>
        )}
      </CardContent>
    </Card>
  );
}
